import { useTheme } from '@/src/context/ThemeContext';
import { getPublicLeaguesUseCase } from '@/src/core/dependencies';
import { logger } from '@/src/core/utils';
import { League } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { ExploreLeaguesScreen } from '@/src/presentation/screens/ExploreLeaguesScreen';
import { Stack, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';

const PAGE_SIZE = 20;

export default function ExploreLeaguesContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadLeagues();
  }, []);

  const loadLeagues = async () => {
    try {
      setLoading(true);
      setPage(0);
      setHasMore(true);
      const data = await getPublicLeaguesUseCase.execute(PAGE_SIZE, 0);
      setLeagues(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      logger.error('ExploreLeagues', 'Failed to load public leagues', { error });
    } finally {
      setLoading(false);
    }
  };

  const loadMoreLeagues = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const data = await getPublicLeaguesUseCase.execute(PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setLeagues((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(data.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('ExploreLeagues', 'Failed to load more public leagues', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLeaguePress = (leagueId: string) => {
    router.push(`/league/${leagueId}`);
  };

  return (
    <>
      <Stack.Screen options={{ title: t('leagues.exploreLeagues') }} />
      <ExploreLeaguesScreen
        leagues={leagues}
        loading={loading}
        loadingMore={loadingMore}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onLeaguePress={handleLeaguePress}
        onRefresh={loadLeagues}
        onLoadMore={loadMoreLeagues}
        colors={colors}
      />
    </>
  );
}
